import React, { useState, useEffect, useMemo } from "react";
import api from "../api";
import { useToast } from "../context/ToastContext";
import { useClientPagination } from "../hooks/useClientPagination";
import TablePagination from "./TablePagination";
import { formatDateTime } from "../utils/formatDate";

const DomainScannerModal = ({ isOpen, onClose, domain, onVerifySuccess }) => {
  const { success: toastSuccess, error: toastError } = useToast();
  const [activeTab, setActiveTab] = useState("verify");
  
  // Verification states
  const [verifying, setVerifying] = useState(false);
  const [checking, setChecking] = useState(false);
  const [verificationDetails, setVerificationDetails] = useState(null);

  // Scan states
  const [scanning, setScanning] = useState(false);
  const [scanRunId, setScanRunId] = useState(null);
  const [scanStatus, setScanStatus] = useState(null);
  const [scanResults, setScanResults] = useState(null);
  const [history, setHistory] = useState([]);
  const [maxDepth, setMaxDepth] = useState(3);
  const [maxPages, setMaxPages] = useState(50);
  
  // Asset filter state
  const [assetFilter, setAssetFilter] = useState("all");

  // Load status on mount/domain change
  useEffect(() => {
    if (isOpen && domain) {
      checkVerificationStatus();
      fetchScanHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, domain]);

  // Polling for active scan
  useEffect(() => {
    let interval = null;
    if (scanning && scanRunId) {
      interval = setInterval(async () => {
        try {
          const res = await api.get(`/api/scanner/run/status/${scanRunId}`);
          setScanStatus(res.data.status);
          
          if (res.data.status === "completed") {
            setScanning(false);
            clearInterval(interval);
            toastSuccess("Website scan completed successfully!");
            fetchResults(scanRunId);
            fetchScanHistory();
          } else if (res.data.status === "failed") {
            setScanning(false);
            clearInterval(interval);
            toastError(res.data.error_message || "Scan run failed.");
          }
        } catch (err) {
          console.error("Polling error:", err);
        }
      }, 2500);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning, scanRunId]);

  const checkVerificationStatus = async () => {
    try {
      const res = await api.get(`/api/scanner/verify/status/${domain.id}`);
      setVerificationDetails(res.data);
      if (res.data.verified) {
        setActiveTab("crawl");
      }
    } catch (err) {
      console.error("Status check failed:", err);
    }
  };

  const generateVerificationToken = async () => {
    try {
      setVerifying(true);
      const res = await api.post(`/api/scanner/verify/generate`, { domainId: domain.id });
      toastSuccess("Verification token generated. Add the TXT record to your DNS settings.");
      setVerificationDetails(prev => ({
        ...prev,
        token: res.data.token,
        txtRecordName: res.data.txtRecordName,
        txtRecordValue: res.data.txtRecordValue,
        expiresAt: res.data.expiresAt,
        status: "pending"
      }));
    } catch (err) {
      toastError("Failed to generate verification token.");
    } finally {
      setVerifying(false);
    }
  };

  const verifyOwnership = async () => {
    try {
      setChecking(true);
      const res = await api.post(`/api/scanner/verify/check`, { domainId: domain.id });
      if (res.data.verified) {
        toastSuccess("Domain ownership verified successfully.");
        setVerificationDetails(prev => ({
          ...prev,
          verified: true,
          status: "verified"
        }));
        if (onVerifySuccess) onVerifySuccess();
        setActiveTab("crawl");
      } else {
        toastError(res.data.message || "TXT record not found yet.");
      }
    } catch (err) {
      toastError("Verification check failed.");
    } finally {
      setChecking(false);
    }
  };

  const startScan = async () => {
    try {
      setScanning(true);
      setScanResults(null);
      toastSuccess(`Starting web crawler scan for ${domain.domain_name}...`);
      const res = await api.post(`/api/scanner/crawl/start`, {
        domainId: domain.id,
        maxDepth,
        maxPages
      });
      setScanRunId(res.data.scanRunId);
      setScanStatus("queued");
    } catch (err) {
      setScanning(false);
      toastError(err.response?.data?.error || "Failed to trigger scan.");
    }
  };

  const fetchResults = async (runId) => {
    try {
      const res = await api.get(`/api/scanner/run/results/${runId}`);
      setScanResults(res.data);
    } catch (err) {
      toastError("Failed to fetch scan results.");
    }
  };

  const fetchScanHistory = async () => {
    try {
      const res = await api.get(`/api/scanner/runs/${domain.id}`);
      setHistory(res.data);
      if (res.data.length > 0 && res.data[0].status === "completed") {
        fetchResults(res.data[0].id);
      }
    } catch (err) {
      console.error("Failed to fetch scan history:", err);
    }
  };

  const filteredAssets = useMemo(() => (
    scanResults?.assets?.filter(a => {
      if (assetFilter === "all") return true;
      return a.asset_type === assetFilter;
    }) || []
  ), [scanResults, assetFilter]);

  const pagesPag = useClientPagination(scanResults?.pages || [], `${scanRunId}-pages`);
  const assetsPag = useClientPagination(filteredAssets, `${scanRunId}-${assetFilter}`);

  if (!isOpen || !domain) return null;

  const isVerified = verificationDetails?.verified;

  // Build recursive node hierarchy tree
  const getTreeData = () => {
    if (!scanResults?.structure) return null;
    const root = { segment: "/", path: "/", type: "root", children: [], pageCount: 0, assetCount: 0 };
    const nodeMap = { "/": root };

    const sortedNodes = [...scanResults.structure].sort((a, b) => a.depth - b.depth);

    for (const n of sortedNodes) {
      if (n.full_path === "/") continue;
      const parentNode = nodeMap[n.parent_path || "/"] || root;
      const newNode = {
        segment: n.path_segment,
        path: n.full_path,
        type: n.node_type,
        pageCount: n.page_count,
        assetCount: n.asset_count,
        children: []
      };
      nodeMap[n.full_path] = newNode;
      parentNode.children.push(newNode);
    }
    return root;
  };

  const renderTreeNode = (node, depth = 0) => {
    return (
      <div key={node.path} style={{ marginLeft: `${depth * 20}px` }} className="py-1">
        <div className="d-flex align-items-center gap-2 border-bottom border-light py-1">
          {node.type === "root" && <span><i className="las la-home text-primary fs-5"></i> <strong className="text-dark">/ (root)</strong></span>}
          {node.type === "folder" && (
            <span>
              <i className="las la-folder text-warning fs-5"></i> 
              <strong className="text-secondary">{node.segment}</strong> 
              <span className="badge bg-light text-dark ms-2 small border">
                {node.pageCount} pages, {node.assetCount} assets
              </span>
            </span>
          )}
          {node.type === "page" && <span><i className="las la-file-alt text-info"></i> <span className="text-dark">{node.segment}</span></span>}
          {node.type === "asset" && <span><i className="las la-paperclip text-success"></i> <span className="text-muted">{node.segment}</span></span>}
        </div>
        {node.children && node.children.map(child => renderTreeNode(child, depth + 1))}
      </div>
    );
  };

  const treeData = getTreeData();

  return (
    <>
      <div className="modal-backdrop fade show z-[1040]"></div>
      <div className="modal show d-block z-[1050]" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content border-0 shadow-lg">
            <div className="modal-header bg-light">
              <div className="d-flex align-items-center gap-2">
                <i className="las la-fingerprint fs-4 text-primary"></i>
                <h5 className="modal-title fw-bold text-dark">Domain Scanner Suite</h5>
                <span className="badge bg-secondary text-light px-2 py-1">{domain.domain_name}</span>
                {isVerified ? (
                  <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-2 py-1 small">
                    <i className="las la-check-circle me-1"></i>Verified
                  </span>
                ) : (
                  <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25 px-2 py-1 small">
                    <i className="las la-lock me-1"></i>Unverified
                  </span>
                )}
              </div>
              <button type="button" className="btn-close" onClick={onClose} disabled={scanning}></button>
            </div>

            <div className="modal-body p-0">
              <div className="row g-0">
                {/* Left tab selectors */}
                <div className="col-md-3 bg-light border-end p-3">
                  <div className="nav flex-column nav-pills gap-2">
                    <button
                      className={`nav-link text-start d-flex align-items-center gap-2 ${activeTab === "verify" ? "active" : ""}`}
                      onClick={() => setActiveTab("verify")}
                    >
                      <i className="las la-shield-alt"></i> Domain Verification
                    </button>
                    <button
                      className={`nav-link text-start d-flex align-items-center gap-2 ${activeTab === "crawl" ? "active" : ""} ${!isVerified ? "disabled" : ""}`}
                      onClick={() => {
                        if (!isVerified) {
                          toastError("Tab locked — Complete ownership verification first");
                          return;
                        }
                        setActiveTab("crawl");
                      }}
                    >
                      <i className="las la-spider"></i> Page & URL Scanner {!isVerified && <i className="las la-lock ms-auto text-muted"></i>}
                    </button>
                    <button
                      className={`nav-link text-start d-flex align-items-center gap-2 ${activeTab === "assets" ? "active" : ""} ${!isVerified ? "disabled" : ""}`}
                      onClick={() => {
                        if (!isVerified) {
                          toastError("Tab locked — Complete ownership verification first");
                          return;
                        }
                        setActiveTab("assets");
                      }}
                    >
                      <i className="las la-file-alt"></i> Public Asset Scanner {!isVerified && <i className="las la-lock ms-auto text-muted"></i>}
                    </button>
                    <button
                      className={`nav-link text-start d-flex align-items-center gap-2 ${activeTab === "structure" ? "active" : ""} ${!isVerified ? "disabled" : ""}`}
                      onClick={() => {
                        if (!isVerified) {
                          toastError("Tab locked — Complete ownership verification first");
                          return;
                        }
                        setActiveTab("structure");
                      }}
                    >
                      <i className="las la-project-diagram"></i> Site Structure Map {!isVerified && <i className="las la-lock ms-auto text-muted"></i>}
                    </button>
                  </div>

                  <div className="mt-4 p-3 bg-white rounded border">
                    <h6 className="fw-bold text-dark mb-2 small text-uppercase">Scanner Settings</h6>
                    <div className="mb-2">
                      <label className="form-label small text-muted mb-1">Max Crawl Depth</label>
                      <select 
                        className="form-select form-select-sm" 
                        value={maxDepth} 
                        onChange={(e) => setMaxDepth(parseInt(e.target.value))}
                        disabled={scanning || !isVerified}
                      >
                        <option value="1">1 (Root only)</option>
                        <option value="2">2 (Standard)</option>
                        <option value="3">3 (Deep)</option>
                        <option value="4">4 (Very Deep)</option>
                      </select>
                    </div>
                    <div>
                      <label className="form-label small text-muted mb-1">Max Pages Cap</label>
                      <select 
                        className="form-select form-select-sm" 
                        value={maxPages} 
                        onChange={(e) => setMaxPages(parseInt(e.target.value))}
                        disabled={scanning || !isVerified}
                      >
                        <option value="10">10 pages</option>
                        <option value="25">25 pages</option>
                        <option value="50">50 pages</option>
                        <option value="100">100 pages</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Right content viewports */}
                <div className="col-md-9 p-4" style={{ maxHeight: "70vh", overflowY: "auto" }}>
                  {/* Tab 1: Verification */}
                  {activeTab === "verify" && (
                    <div>
                      <h5 className="fw-bold text-dark mb-3">Domain Ownership Verification</h5>
                      <p className="text-muted small">
                        To run the scanner, you must prove ownership of the domain by adding a custom DNS TXT record. This protects against scanning unauthorized websites.
                      </p>

                      {isVerified ? (
                        <div className="alert alert-success d-flex align-items-center gap-3">
                          <i className="las la-check-circle fs-2 text-success"></i>
                          <div>
                            <h6 className="fw-bold mb-0">Domain Verified</h6>
                            <p className="small mb-0 text-dark opacity-75">
                              This domain was verified successfully. All scanning features are unlocked.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="card border-light p-4">
                          {!verificationDetails?.token ? (
                            <div className="text-center py-3">
                              <i className="las la-shield-alt fs-1 text-primary mb-3"></i>
                              <h6>Generate Verification Record</h6>
                              <p className="small text-muted">Generate a unique validation token to add to your DNS server settings.</p>
                              <button 
                                className="btn btn-primary px-4 py-2" 
                                onClick={generateVerificationToken} 
                                disabled={verifying}
                              >
                                {verifying ? <i className="las la-spinner la-spin me-1"></i> : null}
                                Generate TXT Record Token
                              </button>
                            </div>
                          ) : (
                            <div>
                              <h6 className="fw-bold text-dark mb-3">DNS TXT Configuration Instructions</h6>
                              <ol className="small text-muted mb-4">
                                <li className="mb-2">Log in to your domain hosting provider control panel (e.g. GoDaddy, Namecheap, Cloudflare).</li>
                                <li className="mb-2">Navigate to your Domain DNS management screen.</li>
                                <li className="mb-2">Create a new record with the following parameters:</li>
                              </ol>

                              <div className="table-responsive mb-4">
                                <table className="table table-bordered bg-light small">
                                  <tbody>
                                    <tr>
                                      <th className="bg-white text-muted" style={{ width: "30%" }}>Record Type</th>
                                      <td className="font-monospace text-dark">TXT</td>
                                    </tr>
                                    <tr>
                                      <th className="bg-white text-muted">Host / Name</th>
                                      <td className="font-monospace text-dark">{verificationDetails.txtRecordName}</td>
                                    </tr>
                                    <tr>
                                      <th className="bg-white text-muted">Value</th>
                                      <td className="font-monospace text-dark text-break">{verificationDetails.txtRecordValue}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>

                              <div className="alert alert-warning small py-2 d-flex align-items-center gap-2">
                                <i className="las la-exclamation-triangle"></i>
                                <span>Note: DNS records can take from 5 minutes up to 48 hours to propagate fully.</span>
                              </div>

                              <div className="d-flex gap-2 justify-content-end mt-4">
                                <button 
                                  className="btn btn-outline-secondary" 
                                  onClick={generateVerificationToken}
                                  disabled={verifying || checking}
                                >
                                  Generate New Token
                                </button>
                                <button 
                                  className="btn btn-success px-4" 
                                  onClick={verifyOwnership}
                                  disabled={checking}
                                >
                                  {checking ? <i className="las la-spinner la-spin me-1"></i> : null}
                                  Verify DNS Ownership
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tab 2: Crawler */}
                  {activeTab === "crawl" && (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="fw-bold text-dark mb-0">Web Page & URL Crawler</h5>
                        <button 
                          className="btn btn-primary px-3" 
                          onClick={startScan}
                          disabled={scanning}
                        >
                          {scanning ? (
                            <span><i className="las la-spinner la-spin me-1"></i> Scanning ({scanStatus})...</span>
                          ) : (
                            <span><i className="las la-sync me-1"></i> Trigger Crawl Scan</span>
                          )}
                        </button>
                      </div>

                      {scanning && (
                        <div className="text-center py-5">
                          <div className="spinner-border text-primary mb-3" style={{ width: "3rem", height: "3rem" }} role="status"></div>
                          <h6>Crawling and scanning domain URLs...</h6>
                          <p className="small text-muted">Parsing HTML structures, compiling lists, checking HTTP response headers.</p>
                        </div>
                      )}

                      {!scanning && scanResults && (
                        <div>
                          <div className="row g-3 mb-4">
                            <div className="col-md-4">
                              <div className="p-3 bg-light rounded border text-center">
                                <div className="text-muted small">Total Pages Discovered</div>
                                <h3 className="fw-bold text-dark mt-1">{scanResults.pages.length}</h3>
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div className="p-3 bg-light rounded border text-center">
                                <div className="text-muted small">Broken Links / Error Pages</div>
                                <h3 className={`fw-bold mt-1 ${scanResults.pages.filter(p => p.is_broken).length > 0 ? "text-danger" : "text-success"}`}>
                                  {scanResults.pages.filter(p => p.is_broken).length}
                                </h3>
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div className="p-3 bg-light rounded border text-center">
                                <div className="text-muted small">Average Page Depth</div>
                                <h3 className="fw-bold text-dark mt-1">
                                  {scanResults.pages.length > 0 
                                    ? (scanResults.pages.reduce((acc, p) => acc + p.depth, 0) / scanResults.pages.length).toFixed(1)
                                    : 0}
                                </h3>
                              </div>
                            </div>
                          </div>

                          <h6 className="fw-bold text-dark mb-3">Crawled Page Directory Table</h6>
                          <div className="table-responsive border rounded bg-white">
                            <table className="table table-hover align-middle mb-0 small">
                              <thead className="table-light">
                                <tr>
                                  <th>Page Title</th>
                                  <th>URL</th>
                                  <th>Status</th>
                                  <th>Depth</th>
                                  <th>Load Time</th>
                                </tr>
                              </thead>
                              <tbody>
                                {pagesPag.items.map(page => (
                                  <tr key={page.id} className={page.is_broken ? "table-danger opacity-75" : ""}>
                                    <td><strong>{page.page_title || "No Title Tag"}</strong></td>
                                    <td><span className="font-monospace text-muted">{page.url}</span></td>
                                    <td>
                                      <span className={`badge ${page.status_code === 200 ? "bg-success" : "bg-danger"}`}>
                                        {page.status_code}
                                      </span>
                                    </td>
                                    <td>{page.depth}</td>
                                    <td>{page.load_time_ms} ms</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <TablePagination {...pagesPag} onPageChange={pagesPag.setCurrentPage} className="px-2" />
                        </div>
                      )}

                      {/* History list for premium UX */}
                      {!scanning && history.length > 1 && (
                        <div className="mt-4 border-top pt-3">
                          <h6 className="fw-bold text-dark mb-2 small text-uppercase text-muted">Past Scans History</h6>
                          <div className="list-group small">
                            {history.slice(1).map(h => (
                              <button
                                key={h.id}
                                className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                onClick={() => fetchResults(h.id)}
                              >
                                <span>
                                  <i className="las la-history me-2 text-muted"></i>
                                  Run #{h.id} — {formatDateTime(h.created_at)}
                                </span>
                                <span className={`badge ${h.status === 'completed' ? 'bg-success' : 'bg-danger'} text-light`}>
                                  {h.status.toUpperCase()} {h.duration_ms ? `(${Math.round(h.duration_ms / 1000)}s)` : ""}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tab 3: Asset Scanner */}
                  {activeTab === "assets" && (
                    <div>
                      <h5 className="fw-bold text-dark mb-3">Public Asset File Audit</h5>
                      
                      {scanning && (
                        <div className="text-center py-5">
                          <div className="spinner-border text-primary mb-3" role="status"></div>
                          <h6>Processing scripts, stylesheet assets, files and resources...</h6>
                        </div>
                      )}

                      {!scanning && scanResults && (
                        <div>
                          {/* Asset counts stats */}
                          <div className="row g-3 mb-4">
                            <div className="col-md-3">
                              <div className="p-2 border rounded text-center bg-white">
                                <div className="small text-muted">Images</div>
                                <h4 className="fw-bold text-dark">{scanResults.assets.filter(a => a.asset_type === "image").length}</h4>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="p-2 border rounded text-center bg-white">
                                <div className="small text-muted">Scripts</div>
                                <h4 className="fw-bold text-dark">{scanResults.assets.filter(a => a.asset_type === "script").length}</h4>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="p-2 border rounded text-center bg-white">
                                <div className="small text-muted">Stylesheets</div>
                                <h4 className="fw-bold text-dark">{scanResults.assets.filter(a => a.asset_type === "stylesheet").length}</h4>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="p-2 border rounded text-center bg-white">
                                <div className="small text-muted">Documents & Fonts</div>
                                <h4 className="fw-bold text-dark">
                                  {scanResults.assets.filter(a => a.asset_type === "document" || a.asset_type === "font").length}
                                </h4>
                              </div>
                            </div>
                          </div>

                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <div className="btn-group btn-group-sm">
                              <button 
                                className={`btn ${assetFilter === "all" ? "btn-secondary" : "btn-outline-secondary"}`}
                                onClick={() => setAssetFilter("all")}
                              >
                                All ({scanResults.assets.length})
                              </button>
                              <button 
                                className={`btn ${assetFilter === "image" ? "btn-secondary" : "btn-outline-secondary"}`}
                                onClick={() => setAssetFilter("image")}
                              >
                                Images
                              </button>
                              <button 
                                className={`btn ${assetFilter === "script" ? "btn-secondary" : "btn-outline-secondary"}`}
                                onClick={() => setAssetFilter("script")}
                              >
                                Scripts
                              </button>
                              <button 
                                className={`btn ${assetFilter === "stylesheet" ? "btn-secondary" : "btn-outline-secondary"}`}
                                onClick={() => setAssetFilter("stylesheet")}
                              >
                                Stylesheets
                              </button>
                              <button 
                                className={`btn ${assetFilter === "document" ? "btn-secondary" : "btn-outline-secondary"}`}
                                onClick={() => setAssetFilter("document")}
                              >
                                Documents
                              </button>
                            </div>

                            <span className="small text-muted">
                              External Assets: <strong>{scanResults.assets.filter(a => a.is_external).length}</strong>
                            </span>
                          </div>

                          <div className="table-responsive border rounded bg-white">
                            <table className="table table-hover align-middle mb-0 small">
                              <thead className="table-light">
                                <tr>
                                  <th>Asset Target Link</th>
                                  <th>Type</th>
                                  <th>MIME Type</th>
                                  <th>Source Referral Page</th>
                                  <th>Host Location</th>
                                </tr>
                              </thead>
                              <tbody>
                                {assetsPag.items.map(asset => (
                                  <tr key={asset.id}>
                                    <td className="text-break"><span className="font-monospace text-dark">{asset.asset_url}</span></td>
                                    <td>
                                      <span className="badge bg-light text-dark border">
                                        {asset.asset_type.toUpperCase()}
                                      </span>
                                    </td>
                                    <td><span className="text-muted font-monospace">{asset.mime_type || "N/A"}</span></td>
                                    <td className="text-break font-monospace text-muted">{asset.parent_page_url}</td>
                                    <td>
                                      {asset.is_external ? (
                                        <span className="badge bg-warning bg-opacity-10 text-warning border px-2 py-1">External</span>
                                      ) : (
                                        <span className="badge bg-success bg-opacity-10 text-success border px-2 py-1">Internal</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                                {assetsPag.items.length === 0 && (
                                  <tr>
                                    <td colSpan="5" className="text-center py-4 text-muted">No assets found for the selected type.</td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                          <TablePagination {...assetsPag} onPageChange={assetsPag.setCurrentPage} className="px-2" />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tab 4: Site Map */}
                  {activeTab === "structure" && (
                    <div>
                      <h5 className="fw-bold text-dark mb-3">Site Architecture Structure Map</h5>
                      <p className="text-muted small">
                        This tree model structures the relative folders, web pages, and asset modules discovered during directory scan crawls.
                      </p>

                      {scanning && (
                        <div className="text-center py-5">
                          <div className="spinner-border text-primary mb-3" role="status"></div>
                          <h6>Mapping folder segment graphs...</h6>
                        </div>
                      )}

                      {!scanning && treeData && (
                        <div className="p-4 bg-light rounded border">
                          <div className="tree-container" style={{ fontFamily: "monospace" }}>
                            {renderTreeNode(treeData)}
                          </div>
                        </div>
                      )}

                      {!scanning && !treeData && (
                        <div className="text-center py-5 text-muted">
                          <i className="las la-project-diagram fs-1 mb-2"></i>
                          <p>No map details found. Trigger a crawler scan to map directory structure paths.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer bg-light">
              <button type="button" className="btn btn-secondary px-4" onClick={onClose} disabled={scanning}>Close</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DomainScannerModal;
